import { useEffect, useRef, useCallback } from "react";

const STORAGE_KEY = "habitia_last_activity";
const SESSION_START_KEY = "habitia_session_start";

const useSessionTimeout = (onTimeout, timeoutMs = 30 * 60 * 1000, absoluteTimeoutMs = 12 * 60 * 60 * 1000) => {
  const timerRef = useRef(null);

  const resetTimer = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY, Date.now().toString());
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onTimeout?.();
    }, timeoutMs);
  }, [onTimeout, timeoutMs]);

  useEffect(() => {
    // Check if session already expired on mount
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const started = sessionStorage.getItem(SESSION_START_KEY);
    if (!started) {
      sessionStorage.setItem(SESSION_START_KEY, Date.now().toString());
    } else {
      const startedAt = parseInt(started, 10);
      if (startedAt > 0 && Date.now() - startedAt > absoluteTimeoutMs) {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(SESSION_START_KEY);
        onTimeout?.();
        return;
      }
    }
    if (stored) {
      const last = parseInt(stored, 10);
      if (last > 0 && Date.now() - last > timeoutMs) {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(SESSION_START_KEY);
        onTimeout?.();
        return;
      }
    }

    // Fresh login or still active — start tracking
    resetTimer();

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((ev) => window.removeEventListener(ev, resetTimer));
    };
  }, [resetTimer, onTimeout, timeoutMs, absoluteTimeoutMs]);
};

export default useSessionTimeout;
